import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { useHistory } from "@core/routing/use-history";
import { useQueryParams } from "@core/routing/use-query-params";
import { networkActionsErrorsManager, patientManager } from "@core/service-container";
import { sendAnalyticsEventPatientListViewed } from "@domain/patient/patient-analytics";
import { hasPatientExtendableProgram } from "@domain/program/program-status/program-status-utils";
import styled from "@emotion/styled";
import { AlertCard, CardType } from "@ui/components/common/alert-card";
import { ConditionalSkeletonProps } from "@ui/components/common/conditional-skeleton";
import { NetworkError } from "@ui/components/common/error";
import { FilterTag } from "@ui/components/common/filter-tag";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { RoutesPath } from "@ui/navigation/routes";
import { PendingPatientListing } from "@ui/pages/home/components/pending-patient-listing";
import { RegisteredPatientListing } from "@ui/pages/home/components/registered-patient-listing";
import {
	EmptyPatientMessage,
	EmptyPatientMessageVariant,
} from "@ui/pages/patient-details/components/empty-patient-message";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { indexOfOrDefault } from "@utils/array-utils";
import { isNotNil } from "@utils/assert";
import _ from "lodash";
import { useObservable } from "micro-observables";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

export enum HomeTabNames {
	Registered = "registered",
	Pending = "pending",
}

const tabIndices = [HomeTabNames.Registered, HomeTabNames.Pending];

export const PatientTabs: React.FC = () => {
	const { format } = useI18n();
	const { isMobile } = useDimensions();
	const { updateHistory } = useHistory<RoutesPath.Home>();
	const { active } = useQueryParams<RoutesPath.Home>();
	const { registeredPatients, pendingPatients } = useObservable(patientManager.allPatients);

	const [activePatientTab, setActivePatientTab] = useState(
		indexOfOrDefault(tabIndices, active, HomeTabNames.Registered),
	);
	const [isFilteringPatientsWithExtensionNeeded, setIsFilteringPatientsWithExtensionNeeded] = useState(false);
	const eventSent = useRef(false);

	const listAllPatients = useCallback(() => patientManager.fetchAllPatients(), []);
	const [fetchPatientData, loadingPatients, errorLoadingPatients] = useAsyncOperation(listAllPatients, {
		initialLoadingState: true,
	});

	const listAllRegisteredPatientCompliance = useCallback(
		() => patientManager.fetchRegisteredPatientGlobalCompliances(),
		[],
	);
	const [fetchPatientCompliances, loadingCompliances, errorLoadingCompliances] = useAsyncOperation(
		listAllRegisteredPatientCompliance,
		{},
	);

	const skeletonProps: ConditionalSkeletonProps = useMemo(
		() => ({ active: loadingPatients, loading: loadingPatients }),
		[loadingPatients],
	);

	useEffect(() => {
		fetchPatientData();
		fetchPatientCompliances();
	}, [fetchPatientCompliances, fetchPatientData]);

	const selectTab = (tabIndex: number) => {
		setActivePatientTab(tabIndex);
	};

	useEffect(() => {
		if (errorLoadingPatients) {
			networkActionsErrorsManager.reportError(errorLoadingPatients, format("home.patientListingError.toast"));
		}
		if (errorLoadingCompliances) {
			networkActionsErrorsManager.reportError(
				errorLoadingCompliances,
				format("home.patientComplianceListingError.toast"),
			);
		}
	}, [errorLoadingCompliances, errorLoadingPatients, format]);

	useEffect(() => {
		if (isNotNil(registeredPatients) && !eventSent.current) {
			sendAnalyticsEventPatientListViewed(
				_.isEmpty(registeredPatients) ? HomeTabNames.Pending : HomeTabNames.Registered,
			);
			eventSent.current = true;
		}
	}, [registeredPatients]);

	const headerStyle = useMemo(
		() => ({
			width: isMobile ? "100%" : "unset",
		}),
		[isMobile],
	);

	const patientsWithExtendableProgram = useMemo(
		() => registeredPatients.filter(hasPatientExtendableProgram),
		[registeredPatients],
	);

	const registeredPatientsData = useMemo(
		() => (isFilteringPatientsWithExtensionNeeded ? patientsWithExtendableProgram : registeredPatients),
		[isFilteringPatientsWithExtensionNeeded, patientsWithExtendableProgram, registeredPatients],
	);

	if (loadingPatients) {
		return <LoadingContainer />;
	}

	if (errorLoadingPatients) {
		return (
			<ErrorContainer>
				<NetworkError
					style={{ width: "100%", padding: "72px 32px" }}
					titleStyle={{ fontSize: "1.5rem" }}
					description={format("home.patientListingError.description")}
					onRefresh={async () => {
						await fetchPatientData();
					}}
				/>
			</ErrorContainer>
		);
	}

	if (registeredPatientsData.length === 0 && pendingPatients.length === 0) {
		return <EmptyPatientMessage variant={EmptyPatientMessageVariant.NoPatients} />;
	}

	return (
		<StyledTabView
			activeIndex={activePatientTab}
			onTabChange={e => {
				sendAnalyticsEventPatientListViewed(tabIndices[e.index]);
				updateHistory({ active: tabIndices[e.index] });
				selectTab(e.index);
			}}
		>
			<TabPanel
				headerStyle={headerStyle}
				header={format("home.registeredPatientsListing.tabName", { count: registeredPatients.length })}
			>
				<Head>
					{isNotNil(patientsWithExtendableProgram) &&
						patientsWithExtendableProgram.length > 0 &&
						!skeletonProps.active && (
							<StyledAlertCard
								title={format("home.registeredPatientsListing.alert.programEnding.title")}
								message={format("home.registeredPatientsListing.alert.programEnding.message", {
									patientNumber: patientsWithExtendableProgram.length,
								})}
								cardType={CardType.Warn}
								textButton={
									isFilteringPatientsWithExtensionNeeded
										? {
												text: format("home.registeredPatientsListing.alert.programEnding.label"),
											}
										: {
												text: format("home.registeredPatientsListing.alert.programEnding.button"),
												onPressed: () => setIsFilteringPatientsWithExtensionNeeded(true),
											}
								}
							/>
						)}
					{isFilteringPatientsWithExtensionNeeded && (
						<FiltersContainer>
							<div className="p-size-s" color={colors.grey}>
								{format("home.registeredPatientsListing.alert.activeFilters.text")}
							</div>
							<FilterTag
								label={format("home.registeredPatientsListing.activeFilter.extensionNeeded")}
								onCrossClicked={() => setIsFilteringPatientsWithExtensionNeeded(false)}
							/>
						</FiltersContainer>
					)}
				</Head>
				{registeredPatientsData.length === 0 ? (
					<EmptyPatientMessage variant={EmptyPatientMessageVariant.NoRegisteredPatients} />
				) : (
					<RegisteredPatientListing
						registeredPatients={registeredPatientsData}
						isLoadingCompliances={loadingCompliances}
					/>
				)}
			</TabPanel>
			<TabPanel
				headerStyle={headerStyle}
				header={format("home.pendingPatientsListing.tabName", { count: pendingPatients.length })}
			>
				{pendingPatients.length === 0 ? (
					<EmptyPatientMessage variant={EmptyPatientMessageVariant.NoPendingPatients} />
				) : (
					<PendingPatientListing pendingPatients={pendingPatients} />
				)}
			</TabPanel>
		</StyledTabView>
	);
};

const ErrorContainer = styled.div`
	${stack(undefined, "center", "stretch")};
	flex-grow: 1;
`;

const StyledTabView = styled(TabView)`
	.p-tabview-nav {
		@media ${physioMeTabletBreakpoint} {
			background-color: ${colors.primaryBackground} !important;
		}
	}

	.p-tabview-nav-link {
		@media ${physioMeTabletBreakpoint} {
			background-color: ${colors.primaryBackground} !important;
			justify-content: center !important;
		}
	}

	.p-tabview-panels {
		@media ${physioMeTabletBreakpoint} {
			padding: 0 !important;
		}
	}
`;

const StyledAlertCard = styled(AlertCard)`
	margin-bottom: 32px;

	@media ${physioMeMobileBreakpoint} {
		margin-bottom: 16px;
	}
`;

const FiltersContainer = styled.div`
	${row(8, "flexstart", "center")};
	margin-bottom: 16px;

	@media ${physioMeMobileBreakpoint} {
		margin-bottom: 8px;
	}
`;

const Head = styled.div`
	@media ${physioMeTabletBreakpoint} {
		margin: 16px 24px;
	}

	@media ${physioMeMobileBreakpoint} {
		margin: 16px 16px;
	}
`;
